/**
 * PostnlRitmonitor — firma bazlı sekmeli gerçek zamanlı ride monitörü.
 *
 * Her aktif PostNL hesabı için ayrı sekme. Backend `GET /api/postnl/ritmonitor`
 * tek call ile tüm tabs'ı döner; her tab kendi son snapshot'ını render eder.
 * Cron job 5 dk'da bir tüm aktif credential'lar için scrape eder.
 *
 * Manuel "Şimdi Yenile" sadece o anki seçili sekmenin firmasını refresh eder.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import PostnlPageTabs from '../components/PostnlPageTabs';
import { api } from '../lib/auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Loader2, AlertCircle, Clock, Truck, Building2 } from 'lucide-react';

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

function ageLabel(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s önce`;
  if (sec < 3600) return `${Math.floor(sec / 60)}dk önce`;
  return `${Math.floor(sec / 3600)}sa önce`;
}

export default function PostnlRitmonitor() {
  const [tabs, setTabs] = useState([]); // [{credential_id, credential_label, snapshot}]
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get('/postnl/ritmonitor');
      const next = res.data?.data?.tabs || [];
      setTabs(next);
      // İlk yüklemede ilk tab'ı aktif et
      setActiveId((prev) => prev && next.find((t) => t.credential_id === prev) ? prev : next[0]?.credential_id || null);
      setError(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTab = useMemo(
    () => tabs.find((t) => t.credential_id === activeId) || null,
    [tabs, activeId],
  );

  const refresh = async () => {
    if (!activeId) return;
    setRefreshing(true);
    setError(null);
    try {
      await api.post('/postnl/ritmonitor/refresh', { credential_id: activeId });
      await load();
      toast.success('Güncellendi');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail;
      setError(msg || 'Refresh başarısız');
      toast.error('Yenileme başarısız');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="postnl-ritmonitor-page">
      <PostnlPageTabs />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="h-6 w-6 text-orange-500" />
            PostNL Ritmonitor
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Her firma için ayrı sekme. Otomatik 5 dk'da bir tüm firmalar yenilenir, ekran 30 sn'de bir kontrol eder.
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing || !activeId}
          className="bg-orange-500 hover:bg-orange-600 gap-2"
          data-testid="postnl-ritmonitor-refresh-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {refreshing ? 'Yenileniyor…' : activeTab ? `${activeTab.credential_label} — Şimdi Yenile` : 'Şimdi Yenile'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Card className="p-4 border-rose-300 bg-rose-50 dark:bg-rose-900/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5" />
            <div className="text-sm text-rose-700">{error}</div>
          </div>
        </Card>
      )}

      {/* Tabs bar */}
      {loading ? (
        <Card className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" />
        </Card>
      ) : tabs.length === 0 ? (
        <Card className="p-12 text-center" data-testid="postnl-ritmonitor-empty">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Aktif PostNL hesabı yok</div>
          <div className="text-xs text-slate-400 mt-1">
            <a href="/dashboard/postnl/accounts" className="text-orange-600 hover:underline">PostNL Hesapları</a> sayfasından firma ekleyin.
          </div>
        </Card>
      ) : (
        <>
          <div className="flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700 pb-px" data-testid="postnl-ritmonitor-tabs">
            {tabs.map((t) => {
              const active = activeId === t.credential_id;
              const snap = t.snapshot;
              const ageSec = snap?.captured_at
                ? Math.floor((now - new Date(snap.captured_at).getTime()) / 1000)
                : null;
              const stale = ageSec != null && ageSec > 6 * 60;
              return (
                <button
                  key={t.credential_id}
                  onClick={() => setActiveId(t.credential_id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
                    active
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                  data-testid={`postnl-ritmonitor-tab-${t.credential_id}`}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {t.credential_label}
                  {snap && (
                    <Badge
                      variant="outline"
                      className={`text-[9px] gap-1 ${stale ? 'border-amber-400 text-amber-700 bg-amber-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'}`}
                    >
                      <Clock className="h-2.5 w-2.5" />
                      {ageLabel(ageSec)}
                    </Badge>
                  )}
                  {!snap && (
                    <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500">
                      veri yok
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Active tab content */}
          {activeTab ? (
            activeTab.snapshot ? (
              <>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Snapshot: <strong className="font-mono">{fmtTime(activeTab.snapshot.captured_at)}</strong></span>
                  <span>Çeken: {activeTab.snapshot.fetched_by_name || '—'}</span>
                </div>
                <RitmonitorTable rides={activeTab.snapshot.rides || []} />
              </>
            ) : (
              <Card className="p-12 text-center">
                <Truck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <div className="text-sm text-slate-600">{activeTab.credential_label} için henüz snapshot yok</div>
                <div className="text-xs text-slate-400 mt-1">"Şimdi Yenile" ile ilk veriyi çekin.</div>
              </Card>
            )
          ) : null}
        </>
      )}
    </div>
  );
}

function RitmonitorTable({ rides }) {
  const sorted = useMemo(
    () => [...rides].sort((a, b) => (a.ritnaam || '').localeCompare(b.ritnaam || '')),
    [rides],
  );
  const totalStops = sorted.reduce((s, r) => s + (r.aantal_stops || 0), 0);
  const remaining = sorted.reduce((s, r) => s + (r.stops_te_doen || 0), 0);
  const done = totalStops - remaining;
  const donePct = totalStops > 0 ? Math.round((done / totalStops) * 100) : 0;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat label="Aktif Ride" value={sorted.length} />
        <SummaryStat label="Toplam Stop" value={totalStops} />
        <SummaryStat label="Tamamlanan" value={done} tone="emerald" />
        <SummaryStat label="Kalan" value={remaining} tone="amber" />
      </div>
      <Card className="p-3 flex items-center gap-3 bg-gradient-to-r from-orange-50 to-emerald-50 dark:from-orange-900/10 dark:to-emerald-900/10">
        <div className="flex-1">
          <div className="text-xs text-slate-600 dark:text-slate-300 mb-1 flex justify-between">
            <span>İlerleme</span>
            <span className="font-mono">{donePct}%</span>
          </div>
          <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-emerald-500 transition-all duration-700"
              style={{ width: `${donePct}%` }}
            />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="postnl-ritmonitor-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Kanaal</th>
                <th className="text-left px-3 py-2.5 font-semibold">Ritnaam</th>
                <th className="text-left px-3 py-2.5 font-semibold">Şoför</th>
                <th className="text-left px-3 py-2.5 font-semibold">Son Aksiyon</th>
                <th className="text-right px-3 py-2.5 font-semibold">Toplam</th>
                <th className="text-right px-3 py-2.5 font-semibold">Kalan</th>
                <th className="text-right px-3 py-2.5 font-semibold">İlerleme</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const total = r.aantal_stops || 0;
                const left = r.stops_te_doen || 0;
                const completed = total - left;
                const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                let rowTone = 'text-slate-700 dark:text-slate-200';
                if (left === 0 && total > 0) rowTone = 'text-emerald-700 dark:text-emerald-300';
                else if (left === total) rowTone = 'text-slate-500';
                return (
                  <tr
                    key={r.ritnaam || i}
                    className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 ${rowTone}`}
                    data-testid={`postnl-ritmonitor-row-${i}`}
                  >
                    <td className="px-3 py-2 text-xs font-mono">{r.distributiekanaal || '—'}</td>
                    <td className="px-3 py-2 font-mono font-medium">{r.ritnaam || '—'}</td>
                    <td className="px-3 py-2">{r.chauffeur || '—'}</td>
                    <td className="px-3 py-2 font-mono text-sm">{r.tijdstip_laatste_actie || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{total}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {left === 0 && total > 0 ? <Badge className="bg-emerald-100 text-emerald-700 border-0">✓</Badge> : left}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${left === 0 && total > 0 ? 'bg-emerald-500' : pct > 60 ? 'bg-orange-500' : 'bg-slate-400'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[10px] tabular-nums w-8 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400 text-xs">Şu an aktif ride yok.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function SummaryStat({ label, value, tone = 'slate' }) {
  const toneCls = {
    slate: 'text-slate-900 dark:text-slate-100',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
  }[tone] || 'text-slate-900';
  return (
    <Card className="p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
    </Card>
  );
}
